import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  CircularProgress,
  Alert,
} from '@mui/material';
import { css } from '@emotion/css';
import { Theme, useTheme } from '@mui/material';
import ContentfulMarkdown from '@components/Contentful/ContentfulMarkdown';

interface UserAgreementModalProps {
  open: boolean;
  onAccept: () => void;
  loading?: boolean;
  content?: string;
  version?: number;
  isLoadingContent?: boolean;
  error?: string | null;
}

const UserAgreementModal: React.FC<UserAgreementModalProps> = ({
  open,
  onAccept,
  loading = false,
  content,
  version,
  isLoadingContent = false,
  error = null,
}) => {
  const theme = useTheme();
  const cls = useStyles(theme);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [scrollProgress, setScrollProgress] = useState(0);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  useEffect(() => {
    // Reset states when modal opens
    if (open) {
      setScrollProgress(0);
      setHasScrolledToBottom(false);
      setIsChecked(false);
    }
  }, [open]);

  const handleScroll = () => {
    if (!scrollContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    const scrollable = scrollHeight - clientHeight;

    if (scrollable <= 0) {
      // Content is shorter than container, consider it "scrolled"
      setScrollProgress(100);
      setHasScrolledToBottom(true);
      return;
    }

    const progress = (scrollTop / scrollable) * 100;
    setScrollProgress(progress);

    // Consider "bottom" reached at 95% to account for rounding
    if (progress >= 95 && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsChecked(event.target.checked);
  };

  const canAccept = hasScrolledToBottom && isChecked && !loading;

  return (
    <Dialog
      fullScreen
      open={open}
      disableEscapeKeyDown
      className={cls.dialog}
    >
      <AppBar position="fixed" color="default" elevation={1}>
        <Toolbar>
          <Typography variant="h6" className={cls.title}>
            User Agreement
          </Typography>
        </Toolbar>
        <LinearProgress
          variant="determinate"
          value={scrollProgress}
          className={cls.progressBar}
        />
      </AppBar>

      <Box className={cls.content}>
        <Box
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className={cls.scrollContainer}
        >
          <Box className={cls.markdownWrapper}>
            {isLoadingContent ? (
              <Box className={cls.loadingContainer}>
                <CircularProgress size={60} />
                <Typography variant="h6" sx={{ marginTop: 3 }}>
                  Loading User Agreement...
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ marginTop: 1 }}>
                  Please wait while we fetch the latest agreement
                </Typography>
              </Box>
            ) : content ? (
              <ContentfulMarkdown content={content} variant="modal" />
            ) : null}
          </Box>
        </Box>

        <Box className={cls.footer}>
          <Box className={cls.footerContent}>
            {error && (
              <Alert severity="error" sx={{ marginBottom: 2 }}>
                {error}
              </Alert>
            )}

            {!hasScrolledToBottom && !error && (
              <Typography variant="body2" color="text.secondary" className={cls.scrollHint}>
                Please scroll to the bottom to continue
              </Typography>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={isChecked}
                  onChange={handleCheckboxChange}
                  disabled={!hasScrolledToBottom || loading || isLoadingContent}
                />
              }
              label={
                <Typography
                  variant="body2"
                  sx={{
                    color: (!hasScrolledToBottom || loading || isLoadingContent) ? 'text.disabled' : 'text.primary'
                  }}
                >
                  I have read and agree to the User Agreement
                </Typography>
              }
            />

            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={onAccept}
              disabled={!canAccept || isLoadingContent}
              fullWidth
              className={cls.acceptButton}
            >
              {loading ? 'Processing...' : 'Agree'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme: Theme) => ({
  dialog: css`
    .MuiDialog-paper {
      background-color: ${theme?.palette?.background?.default};
    }
  `,

  title: css`
    flex-grow: 1;
    font-weight: 600 !important;
  `,

  progressBar: css`
    height: 3px;
  `,

  content: css`
    display: flex;
    flex-direction: column;
    height: 100vh;
    padding-top: 64px; /* Height of AppBar */
  `,

  scrollContainer: css`
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
  `,

  markdownWrapper: css`
    max-width: 800px;
    margin: 0 auto;
    padding: 32px 24px 280px 24px; /* Extra bottom padding for footer overlap */

    h2 {
      text-align: center;
      font-size: 1.33em;
    }
  `,

  loadingContainer: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    text-align: center;
  `,

  footer: css`
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(
      to bottom,
      transparent 0%,
      ${theme?.palette?.background?.default} 20%
    );
    padding-top: 40px;
    z-index: 10;
  `,

  footerContent: css`
    max-width: 800px;
    margin: 0 auto;
    padding: 24px;
    background-color: ${theme?.palette?.background?.paper};
    border-top: 1px solid ${theme?.palette?.divider};
    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    gap: 16px;
  `,

  scrollHint: css`
    text-align: center;
    font-style: italic;
    animation: pulse 2s ease-in-out infinite;

    @keyframes pulse {
      0%, 100% {
        opacity: 1;
      }
      50% {
        opacity: 0.5;
      }
    }
  `,

  acceptButton: css`
    padding: 12px !important;
    font-size: 1rem !important;
    font-weight: 600 !important;
  `,
});

export default UserAgreementModal;
